import {
  AppBarData,
  CompanyOption,
  prepContactsLink,
  prepFollowUpsLink,
  prepHelpCenterLink,
  prepLanguages,
  prepMenuLinks,
  prepPortfolioCompaniesLink,
  prepUser,
} from "./appBarDomain";
import {
  adminDashboard,
  adminSettings,
  companyUrl,
  giveUsFeedback,
  navigateToPortfolio,
  navigateToUserSettings,
  openContactUsUrl,
  openPriceUrl,
} from "../utils/navigation";
import { isMobileView } from "../utils/screen";
import { openCookieSettings } from "../cookies/cookies";
import { changeLanguageAction } from "../i18n/i18nDomain";
import {
  extractPortfolioCompaniesUrl,
  extractPortfolioUrl,
  portfolioAllCompanies,
  PortfolioHeader,
  portfolioInstance,
  SelectedPortfolioOption,
  showPortfolioContacts,
} from "../portfolio/portfolioDomain";
import {
  useDispatch,
  useLanguage,
  useRootStore,
  useShareholdersStore,
  useUserPrefs,
  useUserStore,
} from "../stores/RootStoreContext";
import { compact } from "lodash";
import { CompanyItem, SigningMethod } from "../common/common";
import { navigate } from "../events/eventsDomain";
import { TMessage } from "../i18n/tt";
import { UserSettings } from "../types/UserInfo";
import { when } from "../utils/functions";
import { toDate } from "../utils/Dates";
import { useQuery } from "../utils/queryParams";

function prepPortfolioLinks(
  currentUrl: string,
  portfolio: SelectedPortfolioOption,
  portfolioFollowUpCount: number,
  showFollowUp: boolean,
  showContacts: boolean,
) {
  return compact([
    prepPortfolioCompaniesLink({ currentUrl, portfolio }),
    showFollowUp &&
      prepFollowUpsLink({
        currentUrl,
        portfolio,
        portfolioFollowUpCount,
      }),
    showContacts && showPortfolioContacts(portfolio) && prepContactsLink({ currentUrl, portfolio }),
    showContacts && prepHelpCenterLink({ currentUrl, portfolio }),
  ]);
}

const goToPortfolioTooltip = (portfolio: SelectedPortfolioOption | undefined): TMessage => {
  if (portfolio) {
    return { key: "app-bar.back-to", params: { context: portfolio.label } };
  }
  return { key: "app-bar.go-to", params: { context: portfolioAllCompanies.label } };
};

const getLastVisited = (c: CompanyOption, userSettings: UserSettings | null): number =>
  when(userSettings?.lastVisitedCompanies?.[c.organisationNumber], (ts) => toDate(ts).getTime()) ??
  0;

const getRecentCompanies = (
  selectedCompany: CompanyOption | undefined,
  {
    companies,
    userSettings,
    limit = 10,
  }: { companies: CompanyOption[]; userSettings: UserSettings | null; limit?: number },
): CompanyOption[] => {
  return companies
    .filter(
      (c) =>
        c.organisationNumber !== selectedCompany?.organisationNumber &&
        getLastVisited(c, userSettings),
    )
    .toSorted((a, b) => getLastVisited(b, userSettings) - getLastVisited(a, userSettings))
    .slice(0, limit);
};

export function useAppBar({
  withoutMenu,
  withoutUserSettings,
  isPortfolioPage,
  openPortfolioSettings,
  portfolioHeader,
  hideSubscriptionButton,
  selectedPortfolioOption,
}: {
  withoutMenu?: boolean;
  withoutUserSettings?: boolean;
  isPortfolioPage?: boolean;
  openPortfolioSettings?: () => void;
  portfolioHeader?: PortfolioHeader;
  hideSubscriptionButton?: boolean;
  selectedPortfolioOption?: SelectedPortfolioOption;
} = {}): AppBarData | null {
  const locale = useLanguage();
  const { pathname: currentUrl } = useRootStore().location;
  const history = useRootStore().history;
  const userStore = useUserStore();
  const shareholdersStore = useShareholdersStore();
  const isMobile = isMobileView();
  const dispatch = useDispatch();
  const userPrefs = useUserPrefs();
  const roleId: string | undefined = useQuery().get("roleId") ?? undefined;
  const portfolioState = useRootStore().portfolioState;
  if (!userStore.user) {
    return null;
  }
  const userRole = userStore.userRole;
  const portfolio = selectedPortfolioOption ?? portfolioState?.portfolio ?? portfolioAllCompanies;
  const portfolioUrl = extractPortfolioCompaniesUrl(portfolio);
  const menuLinks = withoutMenu
    ? undefined
    : isPortfolioPage
      ? prepPortfolioLinks(
          currentUrl,
          portfolio,
          userStore.portfolioFollowUpCount,
          userStore.hasAnyAdminRole || userStore.hasBoardRole,
          userStore.hasAnyAdminRole,
        )
      : prepMenuLinks({
          orgNumber: userStore.orgNumber,
          accessType: userStore.accessType,
          currentUrl,
          isMobile,
          portfolioUrl,
          shareholderId: userStore.shareholderId,
          country: userStore?.companyInfo?.company.country,
          subscription: userStore.paidSubscription,
          userPrefs,
          userRole,
          roleId,
          followUpCount: userStore.companyInfo?.followUpCount,
        });
  const selectedCompany = isPortfolioPage
    ? undefined
    : userStore.companies.find(
        (c) =>
          c.organisationNumber === userStore.orgNumber &&
          c.accessType === userStore.accessType &&
          c.shareholderId === userStore.shareholderId,
      );
  return {
    portfolioHeader,
    portfolioUrl,
    backToPortfolioTooltip: isPortfolioPage ? undefined : goToPortfolioTooltip(portfolio),
    selectedCompany,
    selectedPortfolioOption: portfolio,
    languages: prepLanguages(locale),
    user: prepUser(userStore.user),
    links: menuLinks,
    subLinks: menuLinks?.find((l) => l.isActive)?.subLinks,
    helpCenter: isPortfolioPage
      ? undefined
      : prepHelpCenterLink({ currentUrl, icon: "question", portfolio: selectedPortfolioOption }),
    companies: getRecentCompanies(selectedCompany, {
      companies: userStore.companies,
      userSettings: userStore.userSettings,
    }),
    portfolios: userStore.portfolios,
    subscription: userStore.paidSubscription,
    signDocumentCounts: isPortfolioPage ? undefined : userStore.signDocumentCounts,
    actions: {
      addAdministrator: (companyItem: CompanyItem) =>
        userStore.showInviteUserDialog(companyItem, locale),
      changeLanguage: (lang) => {
        dispatch(changeLanguageAction(lang));
        userStore.updateUserLanguage(lang);
      },
      logOut: userStore.logout,
      navigateToContactUs: openContactUsUrl,
      navigateToPortfolio: () => {
        userStore.setPortfolioFollowUpCount(0);
        navigateToPortfolio(history);
      },
      navigateToPrices: openPriceUrl,
      navigateToUserSettings: withoutUserSettings
        ? undefined
        : () => navigateToUserSettings(history),
      openCompanySettings: () => history.push(adminSettings(userStore.orgNumber)),
      openPaymentDialog: hideSubscriptionButton
        ? undefined
        : [navigate(adminSettings(userStore.orgNumber) + "/billing")],
      openHelpCenter: () => {
        if (userStore.orgNumber) {
          history.push(adminDashboard(userStore.orgNumber) + "/help-center");
        } else {
          history.push(portfolioUrl + "/help-center");
        }
      },
      openCookieSettings,
      selectCompany: (c) => {
        userStore.clearCompany();
        userStore.setSelectedCompany({
          organisationNumber: c.organisationNumber,
          accessType: c.accessType,
          shareholderId: c.shareholderId,
        });
        shareholdersStore.clearTrankMap();
        history.push(companyUrl(c.organisationNumber, c.accessType, c.shareholderId));
      },
      upgradeToPro: hideSubscriptionButton ? undefined : userStore.openPaymentDialog,
      navigateToFeedback: giveUsFeedback,
      openCreatePortfolioDialog: userStore.hasAnyAdminRole
        ? () => userStore.openCreatePortfolioDialog()
        : undefined,
      openBatchSigningPage: (signingMethod: SigningMethod) =>
        userStore.navigateToBatchSigningPage(signingMethod, currentUrl),
      filterCompaniesInPortfolio: (type) => {
        userStore.setPortfolioFollowUpCount(0);
        history.replace(extractPortfolioUrl(type, portfolioHeader));
      },
      openPortfolio: ({ id, name, createdAt, createdBy }) => {
        const type = portfolioInstance({ id, name, createdAt, createdBy });
        history.replace(extractPortfolioUrl(type, portfolioHeader));
      },
      openPortfolioSettings,
    },
  };
}
